import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import apiService from '../services/api.service';
import GoldenArrow from './common/GoldenArrow';

export type Drilldown = { title: string; rows?: any[]; metric?: string };
export default function DashboardDrilldown({ selection, range, onClose }: { selection: Drilldown; range: { from: Date | null; to: Date | null }; onClose: () => void }) {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<any[]>([]);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportCount, setExportCount] = useState(0);
  const [companyId] = useState(() => Number(localStorage.getItem('selectedCompanyId')) || undefined);
  const exportExcel = async () => {
    setExporting(true);
    setExportCount(0);
    setError('');
    try {
      const records: any[] = selection.rows ? [...selection.rows] : [];
      if (!selection.rows) {
        let exportPage = 0;
        let hasMore = true;
        while (hasMore) {
          const result = await apiService.get<any>('/pr/approval-timing', {
            metric: selection.metric, page: exportPage,
            from: range.from?.toISOString(), to: range.to?.toISOString(), companyId,
          });
          if (!Array.isArray(result.data?.records) || (result.data.hasMore && !result.data.records.length)) {
            throw new Error('Incomplete export response');
          }
          records.push(...result.data.records);
          setExportCount(records.length);
          hasMore = Boolean(result.data.hasMore);
          exportPage += 1;
        }
      }
      if (!records.length) { setError('No matching PRs to export.'); return; }
      const XLSX = await import('xlsx');
      const workbook = XLSX.utils.book_new();
      const sheet = XLSX.utils.json_to_sheet(records.map(row => ({
        'PR': String(row.prnumber || row.prid),
        'Date': row.prdate ? String(row.prdate).slice(0, 10) : '',
        'Status': row.status || '',
        'Value': Number(row.totalamount ?? row.totalAmount ?? 0),
        'Hours': row.hours == null ? '' : Number(row.hours),
      })));
      sheet['!cols'] = [{ wch: 24 }, { wch: 14 }, { wch: 24 }, { wch: 18 }, { wch: 14 }];
      sheet['!autofilter'] = { ref: sheet['!ref']! };
      XLSX.utils.book_append_sheet(workbook, sheet, 'PR data');
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet([{
        'Dashboard selection': selection.title,
        'From (UTC)': range.from?.toISOString() || 'All dates',
        'To (UTC)': range.to?.toISOString() || '',
        'Company ID': companyId ?? '',
        'Exported records': records.length,
        'Exported at (UTC)': new Date().toISOString(),
      }]), 'Filters');
      XLSX.writeFile(workbook, `Dashboard-${selection.title.replace(/[^a-z0-9-]+/gi, '-').slice(0, 70)}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      setError('Excel export failed. No partial file was downloaded. Please try again.');
    } finally { setExporting(false); }
  };
  useEffect(() => {
    let cancelled = false;
    setError('');
    setRows([]);
    if (selection.rows) { setRows(selection.rows.slice(page * 100, (page + 1) * 100)); setMore(selection.rows.length > (page + 1) * 100); return; }
    setLoading(true);
    void apiService.get<any>('/pr/approval-timing', { metric: selection.metric, page, from: range.from?.toISOString(), to: range.to?.toISOString(), companyId })
      .then(result => { if (!cancelled) { setRows(result.data.records); setMore(result.data.hasMore); } })
      .catch(() => { if (!cancelled) setError('Unable to load the underlying approvals'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, range, selection, companyId]);
  return <Dialog open onClose={exporting ? undefined : onClose} fullWidth maxWidth="lg"><DialogTitle>{selection.title}</DialogTitle><DialogContent dividers>
    <Typography variant="body2" sx={{ mb: 1 }}>Same period and access rules as the dashboard. {selection.rows ? `${selection.rows.length} PRs` : 'Approval timing in hours'}</Typography>
    {error && <Alert severity="error">{error}</Alert>}
    <Table size="small"><TableHead><TableRow>{['PR', 'Date', 'Status', 'Value', 'Hours', ''].map((h, i) => <TableCell key={i}>{h}</TableCell>)}</TableRow></TableHead><TableBody>
      {rows.map(row => <TableRow key={row.prid}><TableCell>{row.prnumber || row.prid}</TableCell><TableCell>{new Date(row.prdate).toLocaleDateString()}</TableCell><TableCell>{row.status}</TableCell><TableCell>{Number(row.totalamount || 0).toLocaleString()}</TableCell><TableCell>{row.hours ?? '—'}</TableCell><TableCell><GoldenArrow prId={Number(row.prid)} /></TableCell></TableRow>)}
      {!rows.length && <TableRow><TableCell colSpan={6}>{loading ? 'Loading…' : 'No matching PRs'}</TableCell></TableRow>}
    </TableBody></Table>
  </DialogContent><DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}><Button variant="contained" disabled={loading || exporting || !rows.length} onClick={exportExcel} sx={{ mr: 'auto' }}>{exporting ? `Exporting ${exportCount} PRs…` : 'Export to Excel'}</Button><Typography variant="caption">Exports all matching PRs, not only this page</Typography><Button disabled={exporting || loading || !page} onClick={() => setPage(p => p - 1)}>Previous</Button><Typography>Page {page + 1}</Typography><Button disabled={exporting || loading || !more} onClick={() => setPage(p => p + 1)}>Next</Button><Button disabled={exporting} onClick={onClose}>Close</Button></DialogActions></Dialog>;
}
